// prettier-ignore
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinct, takeUntil } from 'rxjs/operators';
import { IDataSourceDefinition } from '../../../../../../shared/interfaces/data-source/data-source-definitions/data-source-definition.interface';
import { LAZY_LEAFLET_GETTER } from '../../../../../leaflet/lazy-leaflet.provider';
import { IUIDataSourceItem } from '../../../../data-source-items/interfaces/ui-data-source-item';
import { IViewTileDefinition } from '../../../interfaces/view-tile-definition.interface';

interface IPosition {
  center: [number, number];
  zoom: number;
}

let mapIdCounter = 0;

@Component({
  standalone: false,
  selector: 'suvo-bi-rail-map-view-tile',
  template: `
    <div class="map-container flex-1 d-flex ai-stretch">
      @if (position) {
        @if (renderMap) {
          <div class="map-frame flex-1">
            <div class="flex-1 leaflet-map-host" [id]="mapId"></div>
          </div>
        } @else {
          <div class="skeletal-map d-flex fd-column jc-center ai-center">
            <div class="skeletal-map__glow"></div>
            <p>Loading rail map...</p>
          </div>
        }
      } @else {
        <div class="no-map d-flex fd-row jc-center ai-center">
          <p>Coordinate data unavailable</p>
        </div>
      }
    </div>
  `,
  styleUrls: ['./rail-map-view-tile.component.scss'],
})
export class RailMapViewTileComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() viewTile!: IViewTileDefinition;
  @Input() item!: IUIDataSourceItem;
  @Input() form!: FormGroup;
  @Input() definition!: IDataSourceDefinition;
  @Input() editItemMode!: boolean;

  private readonly L = inject(LAZY_LEAFLET_GETTER)();

  private map: any;
  private mapInitializationInProgress = false;
  private isDestroyed = false;
  locationProperty!: string;

  position: IPosition | null = null;
  renderMap = false;

  mapId = 'railMap' + mapIdCounter++;

  ngOnInit() {
    this.locationProperty = this.viewTile.locationProperty;
    this.position = this.getPosition();
  }

  ngAfterViewInit() {
    this.scheduleMapInitialization();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if ((await this.L) && changes.form) {
      this.form?.valueChanges
        .pipe(
          distinct((value) => value[this.locationProperty]),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(() => this.updatePosition());
    }
  }

  async updatePosition() {
    const L = await this.L;
    if (L) {
      this.position = this.getPosition();

      if (this.position && !this.map) {
        await this.scheduleMapInitialization();
      }

      if (this.position && this.map) {
        this.map.panTo(new L.LatLng(...this.position.center), this.position.zoom);
      }

      if (!this.position && this.map) {
        this.destroyMap();
        this.renderMap = false;
      }
    }
  }

  getPosition(): IPosition | null {
    let position = this.derivePosition('city');

    if (!position) {
      position = this.derivePosition('country');
    }

    return position;
  }

  derivePosition(locationProperty: string): IPosition | null {
    if (locationProperty) {
      if (locationProperty == 'coordinates') {
        return null;
      } else {
        let locationData = (
          this.item?.data[locationProperty] as { data: { coords: [number, number] } }
        )?.data;

        if (!locationData || !locationData?.coords) {
          return null;
        }

        let coords = locationData?.coords;

        let lat = coords[0];
        let lng = coords[1];

        if (isNaN(lat) || isNaN(lng)) {
          return null;
        }

        let defaultMapZoomLevel = this.item.data['defaultMapZoomLevel'] as number;

        let zoom = defaultMapZoomLevel ? defaultMapZoomLevel : 10.5;

        return {
          center: [lat, lng],
          zoom: zoom,
        };
      }
    }

    return null;
  }
  private async scheduleMapInitialization() {
    if (!this.position || this.map || this.mapInitializationInProgress || this.isDestroyed) {
      return;
    }

    this.mapInitializationInProgress = true;

    await this.waitForDomToSettle();

    if (!this.position || this.map || this.isDestroyed) {
      this.mapInitializationInProgress = false;
      return;
    }

    this.renderMap = true;
    this.cdr.detectChanges();

    await this.waitForDomToSettle();
    await this.waitForMapHostSize();

    if (!this.position || this.map || this.isDestroyed) {
      this.mapInitializationInProgress = false;
      return;
    }

    await this.initMap();

    this.mapInitializationInProgress = false;
  }

  private waitForAnimationFrame() {
    return new Promise<void>((resolve) => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => resolve());
      } else {
        setTimeout(() => resolve(), 16);
      }
    });
  }

  private async waitForDomToSettle() {
    await this.waitForAnimationFrame();
    await this.waitForAnimationFrame();
  }

  private async waitForMapHostSize(maxAttempts = 180, minStableFrames = 4) {
    if (typeof document === 'undefined') {
      return;
    }

    let lastWidth = 0;
    let lastHeight = 0;
    let stableFrames = 0;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const host = document.getElementById(this.mapId);

      const width = host?.clientWidth || 0;
      const height = host?.clientHeight || 0;

      // Wait until Leaflet host dimensions are non-zero and stable.
      if (width > 0 && height > 0) {
        if (width === lastWidth && height === lastHeight) {
          stableFrames++;
        } else {
          stableFrames = 1;
        }

        if (stableFrames >= minStableFrames) {
          return;
        }
      } else {
        stableFrames = 0;
      }

      lastWidth = width;
      lastHeight = height;

      await this.waitForAnimationFrame();
    }
  }

  private async initMap() {
    const L = await this.L;

    if (!L || !this.position) {
      return;
    }

    const options = {
      center: this.position.center,
      zoom: this.position.zoom,
    };

    this.map = L.map(this.mapId, options);

    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      className: 'main-layer',
      maxZoom: 18,
      minZoom: 2,
      // attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });
    const openrailwaymap = L.tileLayer(
      'http://{s}.tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png',
      {
        attribution:
          '<a href="https://www.openstreetmap.org/copyright">© OpenStreetMap contributors</a>, Style: <a href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA 2.0</a> <a href="http://www.openrailwaymap.org/">OpenRailwayMap</a> and OpenStreetMap',
        minZoom: 2,
        maxZoom: 18,
        tileSize: 256,
      },
    );

    tiles.addTo(this.map);
    openrailwaymap.addTo(this.map);
    this.map.invalidateSize();
  }

  private destroyMap() {
    if (this.map) {
      this.map.off();
      this.map.remove();
      this.map = null;
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.destroyMap();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
